import { useState } from 'react';
import { USD, magnitude, shortDate, stamp } from '../lib/format';
import { C, EASE_SPRING, glass, ink, typeStyle, zoneStyle } from '../lib/theme';
import type { Filters, LostEvent, Reason } from '../lib/types';
import { ContactSemaphore } from './ContactSemaphore';
import { EventAuditModal } from './EventAuditModal';
import { ReasonTagPicker } from './ReasonTagPicker';
import { originFrom, type Origin } from './ScaleModal';
import { IconAlert, IconCheck, IconChevronDown, IconExternal, IconImage, IconLink, IconPlus, IconSwap, IconTrendDown, IconUndo, IconX } from './icons';

/** Popover de hover compartilhado pelos badges "corrigido"/"retirado" — mesmo texto explicativo nos dois lugares que hoje disparam o alerta visual do card (rodapé preenchido + badge). */
function FlagPopover({ label, text, footer }: { label: string; text: string; footer?: string }) {
  return (
    <div style={{
      position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', zIndex: 30,
      transform: 'translateX(-50%)', transformOrigin: 'bottom center',
      animation: `koInfoPop 0.2s ${EASE_SPRING}`,
      width: 240, padding: '10px 14px', borderRadius: 12, pointerEvents: 'none', textAlign: 'left',
      ...glass,
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.green, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: ink(0.78), lineHeight: 1.4 }}>{text}</div>
      {footer && (
        <div style={{ fontSize: 11.5, color: ink(0.6), marginTop: 6, paddingTop: 6, borderTop: `1px solid ${ink(0.1)}` }}>{footer}</div>
      )}
    </div>
  );
}

export interface EventCorrectionBody { corrected_event_type: 'churn' | 'contraction'; corrected_mrr_lost: number; note?: string | null }

export interface EventCardHandlers {
  onOpenClient: (masterId: string) => void;
  onOpenContacts: (ev: LostEvent, origin: Origin) => void;
  // Evidência agora carrega os handlers de correção junto (pedido explícito:
  // toggle "Corrigir" mora DENTRO do EvidenceModal) — o próprio EventCard já
  // tem `onSetCorrection`/`onClearCorrection` da lista que o renderiza, então
  // só repassa em vez de o EvidenceModal (montado uma vez em App.tsx, sem
  // acesso a nenhuma lista específica) precisar de um caminho próprio.
  onOpenEvidence: (
    ev: LostEvent, origin: Origin,
    onSetCorrection: (ev: LostEvent, body: EventCorrectionBody) => void,
    onClearCorrection: (ev: LostEvent) => void,
  ) => void;
  onAudit: (ev: LostEvent) => void;
  onPickReason: (ev: LostEvent, picked: Reason[]) => void;
  onSetCorrection: (ev: LostEvent, body: EventCorrectionBody) => void;
  onClearCorrection: (ev: LostEvent) => void;
}

/**
 * Linha de evento compacta — uma linha por evento (círculo de tipo, cliente +
 * contexto, valor, mini-ações), não mais o card denso original. Pensada pra
 * colunas estreitas: a lista da Fila a 55% da tela e o cartão lateral de
 * Auditados, os dois precisam de muito mais itens visíveis por scroll do que
 * detalhe por item — o detalhe completo continua a um clique (Client Drawer,
 * Contatos, Evidência).
 */
export function EventCard({ ev, reasons, filters, focused, busy, variant, handlers, cardRef }: {
  ev: LostEvent;
  reasons: Reason[];
  filters: Filters;
  focused: boolean;
  busy: boolean;
  variant: 'fila' | 'confirmados';
  handlers: EventCardHandlers;
  cardRef?: (el: HTMLDivElement | null) => void;
}) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const [detailOrigin, setDetailOrigin] = useState<Origin | null>(null);
  const [migrationHover, setMigrationHover] = useState(false);
  const [correctionHover, setCorrectionHover] = useState(false);
  const [retiredHover, setRetiredHover] = useState(false);
  const hasCorrection = ev.corrected_event_type !== null;
  const isRetired = ev.retired_at !== null;
  // Marca visual única pra "auditado, mas não conta como perda real" —
  // cobre as duas causas possíveis (retirado pela fonte OU correção manual
  // tipo Anulado: registro incorreto, duplicação, falso positivo etc), pedido
  // explícito de não deixar essa diferença sumir em silêncio na lista.
  // Cor por semântica (pedido explícito, 2026-09-14): "não conta como perda"
  // é notícia BOA (dinheiro que não foi embora de verdade) → verde. Perda
  // confirmada de verdade → vermelho. Antes as duas causas de "não conta"
  // ficavam vermelhas (alarme), o que invertia a leitura.
  const flagged = isRetired || hasCorrection;
  // O que a linha mostra é sempre o valor VALENDO — o corrigido quando existe,
  // senão o bruto da Nekt. Sem isso a lista contradizia os KPIs (que já usam
  // COALESCE(corrected, bruto)): um evento anulado continuava exibindo o valor
  // cheio, como aconteceu com o WORLDSEG.
  const effectiveType = ev.corrected_event_type ?? ev.event_type;
  const effectiveMrr = hasCorrection ? ev.corrected_mrr_lost : ev.mrr_lost;
  const zone = zoneStyle(ev.zone);
  const type = typeStyle(effectiveType);
  const isException = ev.reason_is_exception === true;
  const anulado = isException || (hasCorrection && magnitude(effectiveMrr) === 0);
  const confirmado = variant === 'confirmados';
  const mrrColor = anulado ? ink(0.4) : type.color;
  // Perda real de verdade (aba Confirmados, sem flag nenhuma) — a única cor
  // "de alarme" da lista agora; VDI fica neutro de propósito (ainda não é
  // decisão, não é nem bom nem ruim ainda).
  const isConfirmedLoss = confirmado && !flagged && !isException;
  // Só abre o painel expandido quando há sinal de verdade pra conferir —
  // pedido explícito: não atrasar a auditoria do dia a dia de quem não
  // precisa disso. Sem sinal, o clique continua instantâneo, como sempre.
  const hasSignal = Number(ev.related_audited_count) > 0 || hasCorrection;
  const evidenceCount = Number(ev.evidence_count) || 0;
  // Ícone do botão de evidência troca de "+" pro tipo real anexado — imagem
  // tem prioridade sobre link quando há os dois (é o sinal mais forte de
  // prova). O contador some se sobrepondo no canto: -5px de offset cabe no
  // gap de 5px entre botões da fileira sem tocar o vizinho.
  const EvidenceIcon = evidenceCount === 0 ? IconPlus : ev.evidence_has_image ? IconImage : IconLink;

  const subtitleParts = [
    ev.reason_label ? `${isException ? '⚠ ' : ''}${ev.reason_label}${(ev.reason_ids?.length ?? 0) > 1 ? ` +${ev.reason_ids.length - 1}` : ''}` : null,
    hasCorrection && !ev.reason_label ? ev.correction_note : null,
    confirmado && ev.audited_at ? `auditado ${stamp(ev.audited_at)}` : shortDate(ev.event_date),
  ].filter(Boolean);

  const stripeUrl = ev.stripe_subscription_id
    ? `https://dashboard.stripe.com/subscriptions/${ev.stripe_subscription_id}`
    : null;

  return (
    <div
      ref={cardRef}
      data-event-card={ev.id}
      className="ko-row"
      style={{
        position: 'relative', display: 'flex', alignItems: 'center', gap: 12,
        padding: '13px 14px', borderRadius: 14, minWidth: 0,
        // Preenchimento notável (não só a linha fina de antes) pra evento que
        // não conta como perda real — retirado pela fonte OU correção manual
        // (Anulado). Pedido explícito: "preenchimento atrás da lista mais
        // notável" depois do disclaimer original ter ficado sutil demais.
        background: focused
          ? ink(0.08)
          : flagged
            ? 'linear-gradient(100deg, rgba(46,204,113,0.16), rgba(46,204,113,0.05) 55%, transparent 85%)'
            : isConfirmedLoss
              ? 'linear-gradient(100deg, rgba(242,85,90,0.14), rgba(242,85,90,0.04) 55%, transparent 85%)'
              : undefined,
        boxShadow: !focused
          ? (flagged ? `inset 3px 0 0 0 ${C.green}99` : isConfirmedLoss ? `inset 3px 0 0 0 ${C.churn}99` : 'none')
          : 'none',
        opacity: busy ? 0.6 : 1,
        transition: 'opacity 0.3s ease, background 0.2s',
      }}
    >
      <span style={{
        width: 34, height: 34, borderRadius: '50%', flex: 'none', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: type.bg, color: type.color, border: `1px solid ${type.color}3a`,
      }}>{effectiveType === 'churn' ? <IconX size={15} /> : <IconTrendDown size={16} />}</span>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flexWrap: 'wrap' }}>
          <button
            type="button" onClick={() => handlers.onOpenClient(ev.master_id)}
            className="ko-client"
            style={{
              border: 'none', background: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
              fontSize: 14.5, fontWeight: 750, letterSpacing: '-0.01em', color: C.fg,
              minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >{ev.master_id}</button>
          <span style={{
            fontSize: 11.5, fontWeight: 780, letterSpacing: '-0.005em',
            padding: '3px 11px', borderRadius: 99, flex: 'none',
            background: zone.bg, color: zone.color, border: `1px solid ${zone.color}45`,
            boxShadow: `0 0 10px -4px ${zone.color}`,
          }}>{ev.zone ?? '—'}</span>
          {ev.channel === 'parceiro' && (
            <span style={{
              fontSize: 10.5, fontWeight: 780, letterSpacing: '0.04em', textTransform: 'uppercase',
              padding: '3px 9px', borderRadius: 99, flex: 'none',
              background: ink(0.08), color: ink(0.78), border: `1px solid ${ink(0.18)}`,
            }}>{ev.partner_name || 'Parceiro sem nome'}</span>
          )}
          {ev.has_other_channel && (
            <span
              title={ev.channel === 'parceiro'
                ? 'Este cliente também tem perda no canal direto — a auditoria daquele evento fica na visão Direto.'
                : 'Este cliente também tem perda via parceiro — a auditoria daquele evento fica na visão Parceiros.'}
              style={{
                fontSize: 10.5, fontWeight: 750, padding: '3px 9px', borderRadius: 99, flex: 'none', cursor: 'help',
                background: 'transparent', color: ink(0.6), border: `1px dashed ${ink(0.3)}`,
              }}
            >cliente misto</span>
          )}
          {hasCorrection && (
            <span
              onMouseEnter={() => setCorrectionHover(true)}
              onMouseLeave={() => setCorrectionHover(false)}
              style={{
                position: 'relative', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 750,
                padding: '3px 9px 3px 7px', borderRadius: 99, flex: 'none', cursor: 'default',
                background: 'rgba(46,204,113,0.14)', color: C.green, border: '1px solid rgba(46,204,113,0.4)',
              }}
            >
              corrigido
              {correctionHover && (
                <FlagPopover
                  label="Anulado" footer={isRetired ? `Também retirado pela fonte desde ${shortDate(ev.retired_at)}` : undefined}
                  text={ev.correction_note ?? 'Evento reclassificado/zerado manualmente na auditoria — registro incorreto, duplicação ou similar.'}
                />
              )}
            </span>
          )}
          {isRetired && (
            <span
              onMouseEnter={() => setRetiredHover(true)}
              onMouseLeave={() => setRetiredHover(false)}
              style={{
                position: 'relative', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 750,
                padding: '3px 9px 3px 7px', borderRadius: 99, flex: 'none', cursor: 'default',
                background: 'rgba(46,204,113,0.14)', color: C.green, border: '1px solid rgba(46,204,113,0.4)',
              }}
            >
              <IconAlert size={11} />retirado
              {retiredHover && (
                <FlagPopover
                  label="Retirado pela fonte"
                  text={`A Nekt não reconhece mais esta chave desde ${shortDate(ev.retired_at)} — constatação técnica, não conta na Perda Bruta.`}
                  footer={hasCorrection ? `Também tem correção manual: ${ev.correction_note ?? 'sem nota'}` : undefined}
                />
              )}
            </span>
          )}
          {ev.is_migration === true && (
            <span
              onMouseEnter={() => setMigrationHover(true)}
              onMouseLeave={() => setMigrationHover(false)}
              style={{
                position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: '50%', flex: 'none', cursor: 'default',
                background: 'rgba(167,139,250,0.16)', color: C.violet, border: '1px solid rgba(167,139,250,0.42)',
              }}
            >
              <IconSwap size={11} />
              {migrationHover && (
                <div style={{
                  position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', zIndex: 30,
                  transform: 'translateX(-50%) scale(1)', transformOrigin: 'bottom center',
                  animation: `koInfoPop 0.2s ${EASE_SPRING}`,
                  display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
                  padding: '7px 12px', borderRadius: 11, pointerEvents: 'none',
                  ...glass,
                }}>
                  <span style={{ fontSize: 11.5, fontWeight: 800, color: C.violet }}>Migração</span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: ink(0.7), fontVariantNumeric: 'tabular-nums' }}>
                    {USD(magnitude(ev.mrr_lost))}
                  </span>
                </div>
              )}
            </span>
          )}
          {Number(ev.related_audited_count) > 0 && (
            <button
              type="button" onClick={(e) => { e.stopPropagation(); setDetailOrigin(originFrom(e)); }}
              title={`${ev.related_audited_count} outro(s) evento(s) deste cliente já auditado(s) no período — clique pra ver`}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 750,
                padding: '3px 9px 3px 7px', borderRadius: 99, flex: 'none', border: `1px solid ${ink(0.16)}`,
                background: ink(0.06), color: ink(0.65), cursor: 'pointer', font: 'inherit',
              }}
            ><IconLink size={10} />{ev.related_audited_count}</button>
          )}
        </div>
        <div style={{
          fontSize: 12, color: ink(0.48), marginTop: 2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{subtitleParts.join(' · ')}</div>
      </div>

      <div style={{
        fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
        color: mrrColor, textDecoration: anulado ? 'line-through' : 'none',
        flex: 'none', minWidth: 78, textAlign: 'right',
      }}>{USD(magnitude(effectiveMrr))}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 'none' }}>
        <ContactSemaphore
          ev={ev}
          onOpen={(e) => { e.stopPropagation(); handlers.onOpenContacts(ev, { x: e.clientX, y: e.clientY }); }}
        />

        <button
          type="button"
          onClick={() => stripeUrl && window.open(stripeUrl, '_blank', 'noopener,noreferrer')}
          disabled={!stripeUrl}
          title={stripeUrl ? `Abrir ${ev.stripe_subscription_id} no Stripe` : 'stripe_subscription_id nulo'}
          style={{
            width: 31, height: 31, borderRadius: 10, flex: 'none', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${stripeUrl ? 'rgba(74,125,255,0.42)' : ink(0.08)}`,
            background: stripeUrl ? 'rgba(74,125,255,0.18)' : ink(0.03),
            color: stripeUrl ? C.blueSoft : ink(0.22),
            boxShadow: stripeUrl ? '0 0 14px -4px rgba(74,125,255,0.7)' : 'none',
            cursor: stripeUrl ? 'pointer' : 'not-allowed', font: 'inherit',
          }}
        ><IconExternal size={14} /></button>

        <button
          type="button"
          onClick={(e) => { if (hasSignal) setDetailOrigin(originFrom(e)); else handlers.onAudit(ev); }}
          disabled={busy}
          title={hasSignal ? 'Ver detalhe antes de auditar' : confirmado ? 'Reverter pra fila' : 'Auditar'}
          className="ko-primary"
          style={{
            width: 33, height: 33, borderRadius: 11, flex: 'none', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            border: confirmado ? `1px solid ${ink(0.14)}` : 'none',
            cursor: busy ? 'wait' : 'pointer', font: 'inherit',
            background: confirmado ? ink(0.08) : `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`,
            color: confirmado ? ink(0.72) : '#fff',
            boxShadow: confirmado ? 'none' : '0 6px 18px -2px rgba(232,85,48,0.6)',
            opacity: busy ? 0.6 : 1,
            transition: `transform 0.25s ${EASE_SPRING}`,
          }}
        >{confirmado ? <IconUndo size={15} /> : <IconCheck size={16} />}</button>

        <div style={{ position: 'relative', flex: 'none' }}>
          <button
            type="button" className="ko-ghost"
            title={evidenceCount > 0 ? `${evidenceCount} evidência(s) anexada(s)` : 'Adicionar evidência'}
            onClick={(e) => handlers.onOpenEvidence(ev, { x: e.clientX, y: e.clientY }, handlers.onSetCorrection, handlers.onClearCorrection)}
            style={{
              width: 31, height: 31, borderRadius: 10, flex: 'none', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${evidenceCount > 0 ? 'rgba(255,144,112,0.42)' : ink(0.16)}`,
              background: evidenceCount > 0 ? 'rgba(255,144,112,0.14)' : ink(0.07),
              color: evidenceCount > 0 ? C.accent : ink(0.72),
              cursor: 'pointer', font: 'inherit',
            }}
          ><EvidenceIcon size={14} /></button>
          {evidenceCount > 0 && (
            <span style={{
              position: 'absolute', top: -5, right: -5, minWidth: 15, height: 15, padding: '0 3px',
              borderRadius: 99, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
              background: C.accent, color: '#fff', border: `1.5px solid ${C.bgDeep}`,
              pointerEvents: 'none',
            }}>{evidenceCount}</span>
          )}
        </div>

        <div style={{ position: 'relative', flex: 'none' }}>
          <button
            type="button" className="ko-ghost" title="Motivos (tags)"
            onClick={() => setReasonOpen((v) => !v)}
            style={{
              width: 31, height: 31, borderRadius: 10, flex: 'none', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${ev.reason_id ? 'rgba(255,144,112,0.5)' : ink(0.16)}`,
              background: ev.reason_id ? 'rgba(255,107,71,0.16)' : ink(0.07),
              color: ev.reason_id ? C.accent : ink(0.72),
              boxShadow: ev.reason_id ? '0 0 13px -4px rgba(255,144,112,0.8)' : 'none',
              cursor: 'pointer', font: 'inherit',
            }}
          ><IconChevronDown size={13} /></button>
          {reasonOpen && (
            <ReasonTagPicker
              reasons={reasons}
              value={ev.reason_ids?.length ? ev.reason_ids : (ev.reason_id != null ? [ev.reason_id] : [])}
              onChange={(picked) => handlers.onPickReason(ev, picked)}
              onClose={() => setReasonOpen(false)}
            />
          )}
        </div>
      </div>

      {focused && (
        <div style={{
          position: 'absolute', inset: -1, borderRadius: 15,
          border: '1.5px solid rgba(255,144,112,0.75)', boxShadow: '0 0 0 3px rgba(255,107,71,0.14)',
          pointerEvents: 'none',
        }} />
      )}

      {detailOrigin && (
        <EventAuditModal
          ev={ev} reasons={reasons} filters={filters} origin={detailOrigin} confirmado={confirmado}
          onClose={() => setDetailOrigin(null)}
          onAudit={handlers.onAudit} onPickReason={handlers.onPickReason}
          onSetCorrection={handlers.onSetCorrection} onClearCorrection={handlers.onClearCorrection}
        />
      )}
    </div>
  );
}
